import { formatToCurrency } from '~/helpers/number';
import type {
  Company,
  Customer,
  FormattedCompany,
  FormattedCompanyInfo,
  FormattedCustomer,
  FormattedItem,
  FormattedJob,
  FormattedJobData,
  FormattedLogo,
  Job,
  JobItem,
  Logo,
} from './types';

export const formatCompanyInfo = (company: Company): FormattedCompanyInfo => {
  const address = company?.contact_info?.contact_address;
  const formattedPostCode = [address?.city, address?.postcode]
    .filter((value) => value)
    .join(', ');

  const contactInfo = company?.contact_info;

  const bankAccount = company?.bank_account;

  return {
    name: company?.company_name || null,
    addressLineOne: address?.line_1 || null,
    addressLineTwo: address?.line_2 || null,
    postalCode: formattedPostCode || null,
    email: contactInfo?.contact_email || null,
    phone: contactInfo?.contact_phone || null,
    vatNumber: company?.vat_number || null,
    accountName: bankAccount?.account_name || null,
    accountNumber: bankAccount?.account_number || null,
    sortCode: bankAccount?.sort_code || null,
  };
};

export const formatLogo = (logo: Logo | null): FormattedLogo => {
  if (!logo || !logo?.mimetype || !logo?.content || !logo?.name)
    return {
      logo: null,
      name: null,
    };
  const prefix = `data:${logo?.mimetype};base64,`;
  return {
    logo: `${prefix}${logo.content}`,
    name: logo?.name || 'logo',
  };
};

export const formatCompany = (companyInfo: Company): FormattedCompany => {
  const logo = formatLogo(companyInfo?.company_logo);
  const company = formatCompanyInfo(companyInfo);
  return {
    logo,
    company,
  };
};

const formatCustomer = (customer: Customer): FormattedCustomer => {
  const address = customer?.address;
  const formattedPostCode = [address?.city, address?.postcode]
    .filter((value) => value)
    .join(', ');
  const name = [customer?.first_name, customer?.last_name]
    .filter((value) => value)
    .join(' ');
  return {
    name: name || null,
    addressLineOne: address?.line_1 || null,
    addressLineTwo: address?.line_2 || null,
    postalCode: formattedPostCode || null,
    email: customer?.email || null,
    phoneNumber: customer?.phone || null,
  };
};

const formatVat = (vat: string): string | null => {
  if (!vat) return null;
  if (vat?.includes('%')) return `${vat} VAT`;
  if (vat === 'exempted') return 'No VAT';
  if (vat === 'custom') return 'Custom';
  return null;
};

const formatItem = (item: JobItem, index: number): FormattedItem => {
  const quantity = `${item?.quantity / 100}x`;
  const price = formatToCurrency(item?.price?.amount || 0);
  const amount = (item?.price?.amount * item?.quantity) / 100;
  const total = formatToCurrency(amount + (item?.vat?.amount || 0));
  const vat = formatVat(item?.vat_rate);
  const key = `item-${index + 1}`;

  return {
    title: item?.title,
    description: item?.description,
    quantity,
    price,
    vat,
    total,
    key,
  };
};

const formatDate = (date: Date): string | null => {
  if (!date) return null;
  const dateObject = new Date(date);
  const day = `0${dateObject?.getDate()}`.slice(-2);
  const month = `0${dateObject?.getMonth() + 1}`.slice(-2);
  const year = dateObject?.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatJobData = (job: Job): FormattedJobData => {
  const lineItems = job?.job_items.map((item, index) =>
    formatItem(item, index),
  );
  const totals = {
    subTotal: formatToCurrency(job?.total?.amount_vat_exclusive?.amount || 0),
    vatTotal: formatToCurrency(job?.total?.amount_vat?.amount || 0),
    total: formatToCurrency(job?.total?.amount_vat_inclusive?.amount || 0),
  };
  const details = {
    reference: job?.id,
    date: formatDate(job?.metadata?.created_at) as string,
  };
  return {
    details,
    lineItems,
    totals,
  };
};

export const formatJob = (job: Job): FormattedJob => {
  const customer = formatCustomer(job?.customer);
  const jobData = formatJobData(job);
  return {
    title: job?.title,
    description: job?.description,
    customer,
    ...jobData,
  };
};
